import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ShieldCheck, MessageCircle, Clock, CheckCircle2, Landmark, FileText, BookOpen, Flame, HelpCircle,
} from 'lucide-react';
import GlowBackground from '@/components/site/GlowBackground';
import RevealText from '@/components/site/RevealText';
import Marquee from '@/components/site/Marquee';
import SectionHeader from '@/components/site/SectionHeader';
import TrustBar from '@/components/site/TrustBar';
import StatsBand from '@/components/site/StatsBand';
import ProcessTimeline from '@/components/site/ProcessTimeline';
import ConstitutionalRights from '@/components/site/ConstitutionalRights';
import DamagesSection from '@/components/site/DamagesSection';
import MythsFacts from '@/components/site/MythsFacts';
import LandmarkCases from '@/components/site/LandmarkCases';
import { whatsappLink } from '@/lib/constants';
import { PHOTOS } from '@/lib/photos';

const FB_CARDS = [
  { tag: 'MYTH MONDAY', title: 'Withdrawn charges do not automatically mean a claim succeeds.', body: 'The court still examines whether the arrest itself was lawful under the CPA and the Constitution.', icon: Flame, to: '/faq' },
  { tag: 'YOUR RIGHTS', title: 'Section 35 of the Constitution — what it guarantees.', body: 'Silence, reason for arrest, an attorney, and a court appearance within 48 hours.', icon: ShieldCheck, to: '/know-your-rights' },
  { tag: 'CASE EXAMPLE', title: 'Arrested at work, three nights detained. Charges withdrawn.', body: 'Illustrative anonymised example — outcomes vary on the facts of each matter.', icon: BookOpen, to: '/case-studies' },
  { tag: 'FAQ FRIDAY', title: 'Can you claim if you were released the same day?', body: 'Yes — the deprivation of liberty is itself a recognised head of damages, however short.', icon: HelpCircle, to: '/faq' },
];

const INFO_STRIP = [
  { icon: Landmark, title: 'What is an unlawful arrest?', body: 'An arrest is unlawful when the police act without the statutory jurisdictional facts required by section 40 of the Criminal Procedure Act, or without a valid warrant, or without objectively reasonable grounds.' },
  { icon: Clock, title: 'What is unlawful detention?', body: 'Detention beyond what is authorised by law, or in conditions inconsistent with human dignity, or continued after grounds have fallen away, may amount to unlawful detention.' },
  { icon: FileText, title: 'What if the charges were withdrawn?', body: 'Withdrawn charges are relevant but not conclusive. The court still assesses the lawfulness of the arrest and detention on the facts.' },
  { icon: ShieldCheck, title: 'Can SAPS arrest without a warrant?', body: 'Only in the limited circumstances set out in section 40 of the CPA, and only where the jurisdictional facts (as clarified in Sekhoto) are met.' },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden noise">
        <GlowBackground intense />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative mx-auto max-w-7xl px-5 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-[11px] font-inter font-semibold uppercase tracking-[0.24em] text-white/70"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#FFD400] animate-pulse" /> South Africa · Civil Claims Against SAPS · Constitutional Rights
          </motion.div>

          <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <RevealText
                as="h1"
                testId="hero-headline"
                lines={[
                  { text: 'Were you', className: 'text-white' },
                  { text: 'wrongfully arrested', className: 'text-white' },
                  { text: 'by the police?', className: 'text-[#FFD400]' },
                ]}
                className="font-poppins font-black tracking-[-0.03em] text-[11vw] leading-[0.95] sm:text-[9vw] md:text-[7vw] lg:text-[5.4vw] xl:text-[5vw]"
              />
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.55 }}
                className="mt-8 max-w-2xl font-inter text-white/80 text-lg md:text-xl leading-relaxed"
              >
                You could have a valid compensation claim. South African law places the burden on the State to justify every arrest and detention. Find out in <span className="text-white font-semibold">under 3 minutes</span> whether your matter is worth investigating.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center"
              >
                <Link
                  to="/calculator"
                  data-testid="hero-cta-calculate"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#FFD400] px-7 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F] hover:shadow-[0_0_60px_rgba(255,212,0,0.45)] transition-shadow"
                >
                  Start Free Assessment
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/know-your-rights"
                  data-testid="hero-cta-qualify"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-6 py-4 text-sm font-inter font-semibold text-white hover:border-[#39A8FF] transition-colors"
                >
                  Learn if You Qualify <ArrowRight className="h-4 w-4 text-[#39A8FF]" />
                </Link>
              </motion.div>

              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.85 }}
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] font-inter text-white/80"
              >
                {['3-minute assessment', 'No upfront costs', 'Confidential', 'Nationwide'].map((t) => (
                  <li key={t} className="inline-flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#39A8FF]" strokeWidth={2} />{t}
                  </li>
                ))}
              </motion.ul>
            </div>

            <motion.aside
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="lg:col-span-4"
            >
              <div className="relative overflow-hidden rounded-3xl border border-[#39A8FF]/30 bg-gradient-to-br from-[#0d1826]/95 to-[#07111F]/95 backdrop-blur-xl p-8 md:p-10 shadow-[0_0_60px_rgba(10,132,255,0.15)]">
                <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full glow-blue opacity-70" />
                <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full glow-red opacity-50" />
                <div className="relative">
                  <div className="text-base md:text-lg font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400] flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#FFD400] animate-pulse" /> Prescription Alert
                  </div>
                  <div className="mt-6 flex items-start gap-4">
                    <Clock className="h-9 w-9 md:h-10 md:w-10 mt-1 text-[#39A8FF] flex-shrink-0" strokeWidth={1.8} />
                    <p className="font-inter text-white text-lg md:text-xl leading-[1.5]">
                      Civil claims for unlawful arrest are generally subject to a <span className="text-[#FFD400] font-semibold">3-year prescription period</span> under the Prescription Act 68 of 1969 — subject to the facts of your case.
                      <span className="block mt-5 text-[#39A8FF] font-semibold">Don&apos;t delay. Get an assessment as soon as possible.</span>
                      <span className="block mt-5 text-white/80 text-base md:text-lg">This assessment is not legal advice. Each matter is assessed on its own facts and law.</span>
                    </p>
                  </div>
                  <Link
                    to="/calculator"
                    data-testid="hero-aside-cta"
                    className="mt-8 inline-flex w-full items-center justify-between rounded-full bg-[#FFD400] px-6 py-4 text-sm md:text-base font-poppins font-bold uppercase tracking-wider text-[#07111F] hover:shadow-[0_0_40px_rgba(255,212,0,0.4)] transition-shadow"
                  >
                    Start Free Assessment <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </div>
            </motion.aside>
          </div>
        </motion.div>
      </section>

      {/* TRUST BAR — immediately under hero */}
      <TrustBar />

      {/* MARQUEE */}
      <Marquee
        items={[
          'CONSTITUTIONAL RIGHTS',
          'FREE ASSESSMENT',
          'UNLAWFUL ARREST',
          'UNLAWFUL DETENTION',
          'CLAIMS ACROSS SA',
        ]}
      />

      {/* STATS */}
      <StatsBand />

      {/* INFORMATION SECTION — Chapter 01 */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="home-info">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <SectionHeader
                eyebrow="Chapter 01 — Plain English"
                title={<>What the law <span className="text-[#39A8FF]">actually says.</span></>}
                subtitle="Short answers to the questions people ask us most. Every case still depends on its own facts and the applicable South African law."
              />
              <Link
                to="/know-your-rights"
                data-testid="info-more-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-xs font-poppins font-semibold uppercase tracking-wider text-white hover:border-[#39A8FF]"
              >
                Read your rights <ArrowRight className="h-4 w-4 text-[#FFD400]" />
              </Link>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {INFO_STRIP.map((it, i) => (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  data-testid={`info-card-${i}`}
                  className="rounded-2xl border border-white/[0.06] bg-[#0d1826]/50 p-6 md:p-7 hover:border-[#0A84FF]/40 transition-colors"
                >
                  <it.icon className="h-6 w-6 text-[#39A8FF]" strokeWidth={1.5} />
                  <h4 className="mt-5 font-poppins font-semibold text-white text-lg">{it.title}</h4>
                  <p className="mt-2 font-inter text-white/60 text-sm leading-relaxed">{it.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6-STEP PROCESS TIMELINE */}
      <ProcessTimeline />

      {/* CONSTITUTIONAL RIGHTS */}
      <ConstitutionalRights />

      {/* DAMAGES */}
      <DamagesSection />

      {/* MYTHS VS FACTS */}
      <MythsFacts />

      {/* LEADING AUTHORITIES */}
      <LandmarkCases />

      {/* PRESCRIPTION URGENCY */}
      <section className="relative py-24 md:py-32 overflow-hidden border-t border-white/5" data-testid="home-prescription">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-[0.35]" style={{ backgroundImage: `url(${PHOTOS.inkGlow})` }} />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,#07111F_0%,rgba(7,17,31,0.75)_50%,#07111F_100%)]" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 h-[480px] w-[480px] rounded-full glow-red opacity-70" />
          <div className="absolute left-1/4 top-1/4 h-[420px] w-[420px] rounded-full glow-blue opacity-60" />
        </div>
        <div className="relative mx-auto max-w-5xl px-5 md:px-8 text-center">
          <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Chapter 07 — Urgency</div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-poppins font-black tracking-[-0.03em] text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95]"
          >
            Do <span className="text-[#FFD400]">not</span> wait.
          </motion.h2>
          <p className="mt-8 mx-auto max-w-2xl font-inter text-white/75 text-base md:text-lg leading-relaxed">
            Civil claims arising from unlawful arrest and detention are generally subject to a <span className="text-white font-semibold">three-year prescription period</span> under the Prescription Act 68 of 1969. Prescription can start running immediately. Get your free assessment while the door is open.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/calculator"
              data-testid="prescription-cta-calculate"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD400] px-7 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F]"
            >
              Check My Claim <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="prescription-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-4 text-sm font-inter font-semibold text-white"
            >
              <MessageCircle className="h-4 w-4 text-[#39A8FF]" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* FACEBOOK PREVIEW */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="home-fb-preview">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHeader
            eyebrow="Chapter 08 — Reading room"
            title={<>Learn. Then <span className="text-[#39A8FF]">act.</span></>}
            subtitle="Featured content from our education library. Information is general and educational — never legal advice."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FB_CARDS.map((c, i) => (
              <motion.div
                key={c.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`fb-card-${i}`}
              >
                <Link to={c.to} className="group flex h-full flex-col rounded-2xl border border-white/[0.06] bg-[#0d1826]/60 p-6 hover:border-[#0A84FF]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">{c.tag}</span>
                    <c.icon className="h-5 w-5 text-[#39A8FF]" strokeWidth={1.5} />
                  </div>
                  <div className="mt-8 font-poppins font-semibold text-white text-lg leading-snug">{c.title}</div>
                  <div className="mt-3 font-inter text-white/60 text-sm leading-relaxed flex-1">{c.body}</div>
                  <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-inter font-bold uppercase tracking-wider text-white/80 group-hover:text-[#FFD400] transition-colors">
                    Read more <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden border-t border-white/5" data-testid="home-final-cta">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${PHOTOS.wetStreet})` }} />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,#07111F_0%,rgba(7,17,31,0.7)_35%,rgba(7,17,31,0.7)_65%,#07111F_100%)]" />
          <div className="absolute inset-0 mix-blend-screen bg-[radial-gradient(45%_45%_at_30%_50%,rgba(10,132,255,0.35),transparent_70%)]" />
          <div className="absolute inset-0 mix-blend-screen bg-[radial-gradient(45%_45%_at_72%_50%,rgba(255,64,64,0.28),transparent_70%)]" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[720px] w-[720px] rounded-full glow-blue opacity-60" />
        </div>
        <div className="relative mx-auto max-w-6xl px-5 md:px-8 text-center">
          <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Ready when you are</div>
          <RevealText
            as="h2"
            testId="final-cta-headline"
            lines={[
              { text: 'You could have', className: 'text-white' },
              { text: 'a valid claim.', className: 'text-[#FFD400]' },
            ]}
            className="mt-4 font-poppins font-black tracking-[-0.03em] text-[11vw] md:text-[6.5vw] leading-[0.95]"
          />
          <p className="mt-8 mx-auto max-w-2xl font-inter text-white/75 text-base md:text-lg">
            Take the free 3-minute assessment. If your matter has grounds to investigate, a South African attorney will be in touch.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/calculator"
              data-testid="final-cta-calculate"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD400] px-8 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F] hover:shadow-[0_0_80px_rgba(255,212,0,0.45)] transition-shadow"
            >
              Start Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="final-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-inter font-semibold text-white hover:border-[#39A8FF]"
            >
              <MessageCircle className="h-4 w-4 text-[#39A8FF]" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
